import { ProjectCreateLayout } from "@/components/ProjectCreate/layout";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import React, { useEffect, useMemo, useState } from "react";

import { ScrollArea } from "@/components/ui/scroll-area";

import { cn } from "@/lib/utils";
import { track } from "@/utils/events/tracker";
import { ProjectRunMode, ReactFramework } from "@/utils/templateProjects";
import { getAxiosErorrMessage, logAxiosError, useUserId } from "@/utils/utils";
import {
  BitbucketRepo,
  GitLabRepo,
  GitProvider,
} from "@/utils/gitProviderUtils";

import { GitHubLogoIcon } from "@radix-ui/react-icons";
import axios from "axios";
import {
  CheckIcon,
  ChevronDown,
  ChevronUp,
  Loader2,
  PlusIcon,
  Trash,
  X,
} from "lucide-react";

import { NavLink, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import { useSession } from "@clerk/clerk-react";
import AnimatedToggle from "@/components/ui/animated-toggle";
import EnvVariableHandler from "@/components/EnvVariableHandler";
import { useGitProvider } from "@/hooks/useGithubPermissions";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";

interface GitHubOwner {
  organizations_url: string;
  avatar_url: string;
  login: string;
}

interface EnvVariable {
  key: string;
  value: string;
}

interface GitOrg {
  id: string | number;
  name: string;
  avatar_url?: string | null;
  full_path?: string;
}

interface GitHubRepo {
  name: string;
  owner: GitHubOwner;
  full_name: string;
}

interface Props {
  editProjectId?: string;
  editProjectData?: {
    name: string;
    repoPath: string;
    framework: string;
    mainFilePath: string;
    projectSubfolderPath: string;
    srcFolderPath: string;
    customInstallCommand: string;
    devServerPort: string;
    customRunCommand: string;
    customInstallExtraCommand: string;
    nodeVersion: string;
    packageManager: string;
    exampleComponentPath: string;
    disableAutoformat?: boolean;
    auto_install_off: boolean;
    base_branch?: string;
  };
  close: (project?: any, canvas?: any) => void;
  provider?: GitProvider;
}

const SECRET_ENV_VARIABLE_VALUE = "SECRET_ENV_VARIABLE_VALUE";
const DELETE_ENV_VARIABLE_CONSTANT = "DELETE_ENV_VARIABLE_CONSTANT";

const AddEditProject: React.FC<Props> = (props) => {
  const [projectNameInput, setProjectNameInput] = useState("");

  const [isOpen, setIsOpen] = useState(false);

  const [projectPathInput, setProjectPathInput] = useState("");
  const [organizationPathInput, setOrganizationPathInput] = useState("");

  const [projectPathSearch, setProjectPathSearch] = useState("");
  const [organizationPathSearch, setOrganizationPathSearch] = useState("");

  const [projectPathInputOpen, setProjectPathInputOpen] = useState(false);
  const [organizationPathInputOpen, setOrganizationPathInputOpen] =
    useState(false);

  const [frameworkInput, setFrameworkInput] = useState<string>(
    ReactFramework.VITE
  );
  const [mainFilePathInput, setMainFilePathInput] = useState("");
  const [projectSubfolderPathInput, setProjectSubfolderPathInput] =
    useState("");
  const [srcFolderPath, setSrcFolderPath] = useState("");
  const [customInstallCommandInput, setCustomInstallCommandInput] =
    useState("");
  const [devServerPortInput, setDevServerPortInput] = useState("3000");
  const [customRunCommandInput, setCustomRunCommandInput] = useState("");
  const [customInstallExtraCommandInput, setCustomInstallExtraCommandInput] =
    useState("");
  const [nodeVersionInput, setNodeVersionInput] = useState("20");
  const [packageManagerInput, setPackageManagerInput] = useState("npm");
  const [exampleComponentPath, setExampleComponentPath] = useState("");
  const [autoFormatEnabled, setAutoformatEnabled] = useState(true);
  const [autoInstallOff, setAutoInstallOff] = useState(false);
  const [baseBranchInput, setBaseBranchInput] = useState("");

  const [requestedRemix, setRequestedRemix] = useState(false);
  const [requestedNext15, setRequestedNext15] = useState(false);

  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [isInitializing, setIsInitializing] = useState(!!props.editProjectId);

  const { session } = useSession();

  const userId = useUserId();

  const {
    githubPermissionState,
    checkGithubPermissions,
    isPermissionGranted,
    repos,
    reposLoading,
  } = useGitProvider(props.provider);

  // Set GitHub connected state based on permission state
  const isGithubConnected = isPermissionGranted;
  const checkingGithub = githubPermissionState.status === "checking";

  const [envVariablesSet, setEnvVariablesSet] = useState<EnvVariable[]>([
    {
      key: "",
      value: "",
    },
  ]);

  const [installClicked, setInstallClicked] = useState(false);

  const { triggerRefetchProjects } = useSupabase();

  const [gitlabProjectPathInputId, setGitlabProjectPathInputId] = useState<
    number | null
  >(null);

  const isGitlab = props.provider === GitProvider.GITLAB;
  const isBitbucket = props.provider === GitProvider.BITBUCKET;

  const organizations = useMemo(() => {
    if (isGitlab) {
      const orgMap: Record<string | number, GitOrg> = {};
      repos.forEach((repo: GitLabRepo) => {
        if (repo && typeof repo === "object" && repo?.namespace) {
          const namespaceId = repo.namespace.id;
          orgMap[namespaceId] = {
            id: namespaceId,
            name: repo.namespace.name,
            avatar_url: repo.namespace.avatar_url,
            full_path: repo.namespace.full_path,
          };
        }
      });
      return Object.values(orgMap);
    } else if (isBitbucket) {
      const orgMap: Record<string | number, GitOrg> = {};
      repos.forEach((repo: BitbucketRepo) => {
        if (repo && typeof repo === "object" && repo?.full_name) {
          const workspace = repo.full_name.split("/")[0];
          orgMap[workspace] = {
            id: workspace,
            name: workspace,
            avatar_url: repo?.links?.avatar?.href,
          };
        }
      });
      return Object.values(orgMap);
    } else {
      const orgMap: Record<
        string,
        { id: string; name: string; avatar_url?: string | null }
      > = {};
      repos.forEach((repo: GitHubRepo) => {
        if (repo.owner?.login) {
          orgMap[repo.owner.login] = {
            id: repo.owner.login,
            name: repo.owner.login,
            avatar_url: repo.owner.avatar_url,
          };
        }
      });
      return Object.values(orgMap);
    }
  }, [repos, isGitlab, isBitbucket]);

  const filteredReposByOrganization = useMemo(() => {
    if (isGitlab) {
      if (!organizationPathInput) return repos;
      return repos.filter(
        (repo: GitLabRepo) =>
          repo.namespace &&
          (String(repo.namespace.id) === String(organizationPathInput) ||
            repo.namespace.name === organizationPathInput)
      );
    }
    if (isBitbucket) {
      if (!organizationPathInput) return repos;
      return repos.filter(
        (repo: BitbucketRepo) =>
          repo.full_name.split("/")[0] === organizationPathInput
      );
    }
    if (!organizationPathInput) return repos;
    return repos.filter(
      (repo: GitHubRepo) =>
        repo.owner?.login?.toLowerCase() === organizationPathInput.toLowerCase()
    );
  }, [repos, organizationPathInput, isGitlab, isBitbucket]);

  const inputsAndSetters = [
    {
      label: "",
      value: organizationPathInput,
      setter: setOrganizationPathInput,
      optional: false,
      editableOnEdit: false,
      infoTooltip: "user/repo",
      isOrganization: true,
      renderHint: () => {
        return null;
      },
      renderInput: () => {
        if (reposLoading) {
          return (
            <Input
              disabled={true}
              value="Loading Organizations..."
              className="text-ellipsis text-secondary-n-50 text-xs line-clamp-1 w-full rounded-md h-8 justify-between px-2 font-normal bg-secondary-n-950 border border-transparent hover:border-secondary-n-600"
            />
          );
        }

        if (props.editProjectId || !organizations?.length) {
          return (
            <Input
              value={organizationPathInput}
              onChange={(e) => {
                setOrganizationPathInput(e.target.value);
              }}
              disabled={Boolean(props.editProjectId)}
              className={cn(
                "w-full rounded-md h-8 justify-between px-2 font-normal bg-secondary-n-950 text-xs border border-transparent hover:border-secondary-n-600"
              )}
            />
          );
        }

        const selectedOrg = organizations.find(
          (org) =>
            String(org.id).toLowerCase() ===
            String(organizationPathInput).toLowerCase()
        );

        return (
          <div className="flex flex-col space-y-0.5">
            <Popover
              open={organizationPathInputOpen}
              onOpenChange={setOrganizationPathInputOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="default"
                  role="combobox"
                  style={{
                    borderWidth: "1px",
                    borderStyle: "solid",
                  }}
                  className={cn(
                    "w-full rounded-md h-8 justify-between px-2 font-normal border border-transparent hover:border-secondary-n-600 bg-secondary-n-950",
                    organizationPathInput && "text-md"
                  )}
                >
                  <div className="flex items-center gap-1">
                    {selectedOrg?.avatar_url ? (
                      <img
                        src={selectedOrg.avatar_url}
                        className="w-5 h-5 object-cover rounded-md"
                        alt=""
                      />
                    ) : (
                      <GitHubLogoIcon className="text-secondary-n-600" />
                    )}
                    <span
                      className={cn(
                        "text-ellipsis  text-secondary-n-50 text-xs line-clamp-1 text-left",
                        !organizationPathInput && "text-secondary-n-600"
                      )}
                    >
                      {selectedOrg?.name || "Choose one"}
                    </span>
                  </div>

                  {organizationPathInputOpen ? (
                    <ChevronUp className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  ) : (
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[250px] py-2 px-0 z-[3010] rounded-xl"
              >
                <Command className="pr-0 px-2">
                  <CommandInput
                    placeholder="Search organizations..."
                    value={organizationPathSearch}
                    onValueChange={setOrganizationPathSearch}
                    className="text-xs placeholder:text-xs placeholder:text-secondary-n-600"
                  />
                  <CommandList className="mt-2">
                    <CommandGroup className="overflow-auto pr-0">
                      {organizations
                        .filter((org) => {
                          if (!organizationPathSearch) return true;
                          return (org as GitOrg).name
                            ?.toLowerCase()
                            .includes(organizationPathSearch.toLowerCase());
                        })
                        .map((org) => {
                          return (
                            <CommandItem
                              className="text-xs px-2 gap-2 transition-colors duration-300 data-[selected=true]:bg-secondary-n-900 hover:bg-secondary-n-900"
                              key={String(org.id).toLowerCase()}
                              value={String(org.id).toLowerCase()}
                              onSelect={(currentValue) => {
                                setOrganizationPathInput(
                                  String(currentValue).toLowerCase()
                                );
                                setProjectPathInput("");
                                setOrganizationPathInputOpen(false);
                              }}
                            >
                              {org.avatar_url ? (
                                <img
                                  src={org.avatar_url}
                                  className="w-5 h-5 object-cover rounded-md"
                                />
                              ) : (
                                <GitHubLogoIcon className="w-5 h-5 text-secondary-n-600" />
                              )}
                              {org.name}
                              <CheckIcon
                                className={cn(
                                  "ml-auto h-4 w-4",
                                  String(
                                    organizationPathInput
                                  ).toLowerCase() ===
                                    String(org.id).toLowerCase()
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                            </CommandItem>
                          );
                        })}
                      {organizationPathSearch && (
                        <CommandItem
                          className="text-xs"
                          value={organizationPathSearch}
                          onSelect={(currentValue) => {
                            setOrganizationPathInput(currentValue);
                            setOrganizationPathInputOpen(false);
                          }}
                        >
                          {organizationPathSearch}
                          <CheckIcon
                            className={cn(
                              "ml-auto h-4 w-4",
                              String(organizationPathInput).toLowerCase() ===
                                organizationPathSearch.toLowerCase()
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      )}
                    </CommandGroup>
                  </CommandList>
                </Command>
                {!props.editProjectId &&
                  props.provider === GitProvider.GITHUB && (
                    <div className="pt-2 border-t border-secondary-n-900">
                      <p className="text-center text-secondary-n-400 text-2xs font-medium">
                        Don't see the organization you're looking for?{" "}
                        <NavLink
                          className="text-2xs inline-block text-primary-n-600 font-bold hover:underline"
                          to="https://github.com/apps/tempo-labs-ai/installations/new"
                          target="_blank"
                        >
                          Install Tempo Github app
                        </NavLink>
                      </p>
                    </div>
                  )}
              </PopoverContent>
            </Popover>
          </div>
        );
      },
    },
    {
      label: "",
      value: projectPathInput,
      setter: setProjectPathInput,
      optional: false,
      editableOnEdit: false,
      infoTooltip: "user/repo",
      isRepository: true,
      renderHint: () => {
        return null;
      },
      renderInput: () => {
        if (reposLoading) {
          return (
            <Input
              disabled={true}
              value="Loading Repos..."
              className="text-ellipsis text-secondary-n-50 text-xs line-clamp-1 w-full rounded-md h-8 justify-between px-2 font-normal bg-secondary-n-950 border border-transparent hover:border-secondary-n-600"
            />
          );
        }

        if (props.editProjectId || !repos?.length) {
          return (
            <Input
              value={projectPathInput}
              onChange={(e) => {
                setProjectPathInput(e.target.value);
              }}
              disabled={Boolean(props.editProjectId)}
              className={cn(
                "w-full rounded-md h-8 justify-between px-2 font-normal bg-secondary-n-950 text-xs border border-transparent hover:border-secondary-n-600"
              )}
            />
          );
        }

        return (
          <div className="flex flex-col space-y-0.5">
            <Popover
              open={projectPathInputOpen}
              onOpenChange={setProjectPathInputOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="default"
                  role="combobox"
                  style={{
                    borderWidth: "1px",
                    borderStyle: "solid",
                  }}
                  className={cn(
                    "w-full rounded-md h-8 justify-between px-2 font-normal bg-secondary-n-950 text-xs border border-transparent hover:border-secondary-n-600",
                    projectPathInput && "text-md"
                  )}
                >
                  <span
                    className={cn(
                      "text-ellipsis text-secondary-n-50 text-xs line-clamp-1 text-left",
                      !projectPathInput && "text-secondary-n-600"
                    )}
                  >
                    {projectPathInput
                      ? projectPathInput
                      : "Select a repository"}
                  </span>

                  {projectPathInputOpen ? (
                    <ChevronUp className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  ) : (
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[250px] py-2 px-0 z-[3010] rounded-xl"
              >
                <Command className="px-2">
                  <CommandInput
                    placeholder="Search repository..."
                    value={projectPathSearch}
                    onValueChange={setProjectPathSearch}
                    className="text-xs placeholder:text-xs placeholder:text-secondary-n-600"
                  />
                  <CommandList className="mt-2">
                    <CommandGroup className="overflow-auto">
                      {filteredReposByOrganization
                        .filter((repo) => {
                          if (!projectPathSearch) return true;
                          return repo.name
                            ?.toLowerCase()
                            .includes(projectPathSearch.toLowerCase());
                        })
                        .map((repo) => (
                          <CommandItem
                            className="text-xs px-2 transition-colors duration-300 data-[selected=true]:bg-secondary-n-900 hover:bg-secondary-n-900"
                            key={repo.name}
                            value={repo.name}
                            onSelect={(currentValue) => {
                              if (props.provider === GitProvider.GITLAB) {
                                setGitlabProjectPathInputId(repo.id as number);
                              } else if (
                                props.provider === GitProvider.BITBUCKET
                              ) {
                                setOrganizationPathInput(
                                  repo.full_name?.split("/")[0] || ""
                                );
                              }
                              setProjectPathInput(currentValue);
                              setProjectPathInputOpen(false);
                            }}
                          >
                            {repo.name}
                            <CheckIcon
                              className={cn(
                                "ml-auto h-4 w-4",
                                projectPathInput === repo.name
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                          </CommandItem>
                        ))}
                      {projectPathSearch && (
                        <CommandItem
                          className="text-xs"
                          value={projectPathSearch}
                          onSelect={(currentValue) => {
                            setProjectPathInput(currentValue);
                            setProjectPathInputOpen(false);
                          }}
                        >
                          {projectPathSearch}
                          <CheckIcon
                            className={cn(
                              "ml-auto h-4 w-4",
                              projectPathInput === projectPathSearch
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      )}
                    </CommandGroup>
                  </CommandList>
                </Command>
                {!props.editProjectId &&
                  props.provider === GitProvider.GITHUB && (
                    <div className="pt-2 border-t border-secondary-n-900">
                      <p className="text-center text-secondary-n-400 text-2xs font-medium">
                        Don't see the repository you're looking for?{" "}
                        <NavLink
                          className="text-2xs inline-block text-primary-n-600 font-bold hover:underline"
                          to="https://github.com/apps/tempo-labs-ai/installations/new"
                          target="_blank"
                        >
                          Install Tempo Github app
                        </NavLink>
                      </p>
                    </div>
                  )}
              </PopoverContent>
            </Popover>
          </div>
        );
      },
    },
    {
      label: "Project Name",
      value: projectNameInput,
      setter: setProjectNameInput,
      optional: false,
      editableOnEdit: true,
      infoTooltip: "Project name",
    },
    {
      label: "Node Version ",
      value: nodeVersionInput,
      setter: setNodeVersionInput,
      optional: false,
      editableOnEdit: true,
      infoTooltip: "18 by default -> translates to node:18-alpine",
      renderInput: () => (
        <Select
          onValueChange={(val) => {
            setNodeVersionInput(val);
          }}
          value={nodeVersionInput || undefined}
        >
          <SelectTrigger
            hideIcon
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
            }}
            className={
              "w-full rounded-md text-xs h-8 justify-between px-2 font-normal border border-transparent hover:border-secondary-n-600 bg-secondary-n-950"
            }
          >
            <SelectValue
              className="text-xs text-secondary-n-600"
              placeholder="Choose Node Version"
            />
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={"20"}>20</SelectItem>
            <SelectItem value={"18"}>18</SelectItem>
            <SelectItem value={"16"}>16</SelectItem>
            <SelectItem value={"14"}>14</SelectItem>
            <SelectItem value={"12"}>12</SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      label: "Framework",
      value: frameworkInput,
      setter: setFrameworkInput,
      optional: false,
      editableOnEdit: true,
      infoTooltip: "Select your framework",
      renderInput: () => (
        <Select
          onValueChange={(val) => {
            if (val === ReactFramework.REMIX) {
              return;
            }

            setFrameworkInput(val);
          }}
          value={frameworkInput || undefined}
        >
          <SelectTrigger
            hideIcon
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
            }}
            className="text-xs w-full rounded-md h-8 justify-between px-3 font-normal bg-secondary-n-950 border border-transparent hover:border-secondary-n-600"
          >
            <SelectValue
              className="text-xs text-secondary-n-600"
              placeholder="Choose Framework"
            />
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ReactFramework.NEXT_JS}>
              Next JS (Pages Router)
            </SelectItem>
            <SelectItem value={ReactFramework.NEXT_JS_APP_ROUTER}>
              Next JS (App Router)
            </SelectItem>
            <SelectItem value={ReactFramework.VITE}>Vite</SelectItem>
            <SelectItem value={ReactFramework.VITE_REACT_ROUTER_V7}>
              Vite React Router 7
            </SelectItem>
            <SelectItem value={ReactFramework.VITE_WITH_VIKE}>
              Vite with Vike
            </SelectItem>
            <SelectItem value={ReactFramework.EXPO}>Expo</SelectItem>
            <SelectItem disabled={true} value={""} className="!opacity-100">
              <div className="flex flex-row items-center justify-between gap-2">
                <span className="opacity-50">Remix (Coming Soon)</span>
                <Button
                  variant="primary"
                  size="xs"
                  className="pointer-events-auto !opacity-100 gap-1"
                  onClick={() => {
                    setRequestedRemix(true);
                    track("REQUEST_REMIX");
                  }}
                  disabled={requestedRemix}
                >
                  {requestedRemix && <CheckIcon className="w-3 h-3" />}
                  <span>{requestedRemix ? "Requested" : "Request"}</span>
                </Button>
              </div>
            </SelectItem>
            <SelectItem disabled={true} value={""} className="!opacity-100">
              <div className="flex flex-row items-center justify-between gap-2">
                <span className="opacity-50">NextJS 15 (Coming Soon)</span>
                <Button
                  variant="primary"
                  size="xs"
                  className="pointer-events-auto !opacity-100 gap-1"
                  onClick={() => {
                    setRequestedNext15(true);
                    track("REQUEST_NEXT_15");
                  }}
                  disabled={requestedNext15}
                >
                  {requestedNext15 && <CheckIcon className="w-3 h-3" />}
                  <span>{requestedNext15 ? "Requested" : "Request"}</span>
                </Button>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      ),
    },

    {
      label: "Package Manager",
      value: packageManagerInput,
      setter: setPackageManagerInput,
      optional: false,
      editableOnEdit: true,
      renderInput: () => (
        <Select
          onValueChange={(val) => {
            setPackageManagerInput(val);
          }}
          value={packageManagerInput || undefined}
        >
          <SelectTrigger
            hideIcon
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
            }}
            className="text-xs w-full rounded-md h-8 justify-between px-3 font-normal bg-secondary-n-950 border border-transparent hover:border-secondary-n-600"
          >
            <SelectValue
              className="text-xs text-secondary-n-600"
              placeholder="Choose Package Manager"
            />
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={"npm"}>npm</SelectItem>
            <SelectItem value={"yarn"}>yarn</SelectItem>
            <SelectItem value={"bun"}>bun</SelectItem>
            <SelectItem value={"pnpm"}>pnpm</SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      label: "Project Subfolder Path",
      value: projectSubfolderPathInput,
      setter: setProjectSubfolderPathInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "projects/client",
    },
    {
      label: "Main File Path",
      value: mainFilePathInput,
      setter: setMainFilePathInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "client/pages/_app.tsx",
    },
    {
      label: "Src File Path",
      value: srcFolderPath,
      setter: setSrcFolderPath,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "/client/src",
    },
    {
      label: "Example Component Path",
      value: exampleComponentPath,
      setter: setExampleComponentPath,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "/src/components/header.tsx",
    },
    {
      label: "Base Git Branch",
      value: baseBranchInput,
      setter: setBaseBranchInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "(dev)",
    },

    {
      label: "Dev Server Port",
      value: devServerPortInput,
      setter: setDevServerPortInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "3000",
    },
    {
      label: "Custom Install Command",
      value: customInstallCommandInput,
      setter: setCustomInstallCommandInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "npm run install-all-deps",
    },
    {
      label: "Custom Run Command",
      value: customRunCommandInput,
      setter: setCustomRunCommandInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "npm run client-start",
    },
    {
      label: "Custom Install Libraries Command",
      value: customInstallExtraCommandInput,
      setter: setCustomInstallExtraCommandInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "npm run add-and-log",
    },

    {
      label: "Automatically format all changes in Tempo",
      value: autoFormatEnabled,
      setter: setAutoformatEnabled,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "Automatically format all changes in Tempo",
      renderInput: () => {
        return (
          <AnimatedToggle
            icon={
              <X
                className={cn(
                  "absolute top-[3px] left-[4px] w-3 h-3 text-xs font-medium"
                )}
              />
            }
            isOn={autoFormatEnabled}
            onChange={setAutoformatEnabled}
          />
        );
      },
    },
    {
      label: "Automatically install the dev tools package",
      value: autoInstallOff,
      setter: setAutoInstallOff,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "Automatically install the dev tools package",
      renderInput: () => {
        return (
          <AnimatedToggle
            icon={
              <X
                className={cn(
                  "absolute top-[3px] left-[4px] w-3 h-3 text-xs font-medium"
                )}
              />
            }
            isOn={autoInstallOff}
            onChange={setAutoInstallOff}
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (props.editProjectData) {
      session?.getToken().then((supabaseJWT) => {
        axios
          .get(
            `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/${
              props.editProjectId
            }/envVariables`,
            {
              headers: {
                Authorization: `Bearer ${supabaseJWT}`,
              },
            }
          )
          .then((res) => {
            const newEnvVariables: EnvVariable[] = [];
            Object.keys(res.data).forEach((key) => {
              newEnvVariables.push({
                key,
                value: SECRET_ENV_VARIABLE_VALUE,
              });
            });
            newEnvVariables.push({
              key: "",
              value: "",
            });

            setEnvVariablesSet(newEnvVariables);
          })
          .catch(() => {
            toast.error("Failed to fetch env variables for project");
          })
          .finally(() => {
            setIsInitializing(false);
          });
      });

      setProjectNameInput(props.editProjectData.name);
      setProjectPathInput(props.editProjectData.repoPath);
      setFrameworkInput(props.editProjectData.framework);
      setMainFilePathInput(props.editProjectData.mainFilePath);
      setProjectSubfolderPathInput(props.editProjectData.projectSubfolderPath);
      setSrcFolderPath(props.editProjectData.srcFolderPath);
      setCustomInstallCommandInput(props.editProjectData.customInstallCommand);
      setDevServerPortInput(props.editProjectData.devServerPort || "3000");
      setCustomRunCommandInput(props.editProjectData.customRunCommand);
      setCustomInstallExtraCommandInput(
        props.editProjectData.customInstallExtraCommand
      );
      setNodeVersionInput(props.editProjectData.nodeVersion || "18");
      setPackageManagerInput(props.editProjectData.packageManager || "npm");
      setExampleComponentPath(props.editProjectData.exampleComponentPath);
      setAutoformatEnabled(props.editProjectData.disableAutoformat !== true);
      setAutoInstallOff(props.editProjectData.auto_install_off);
      setBaseBranchInput(props.editProjectData.base_branch || "");
    }
  }, [props.editProjectData, props.editProjectId, Boolean(session)]);

  useEffect(() => {
    if (session && userId && props.provider === GitProvider.GITHUB) {
      checkGithubPermissions(true);
    }
  }, [Boolean(session), userId, props.provider, checkGithubPermissions]);

  const createProject = () => {
    if (!projectNameInput) {
      toast.error("Missing project name input");
      return;
    }

    if (!projectPathInput) {
      toast.error("Missing project path input");
      return;
    }

    if (!frameworkInput) {
      toast.error("Missing framework input");
      return;
    }

    if (!nodeVersionInput) {
      toast.error("Missing node version input");
      return;
    }

    if (!packageManagerInput) {
      toast.error("Missing package manager input");
      return;
    }

    let urlToHit = `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/add`;
    if (isAdd) {
      track("ADD_PROJECT", { type: "CONNECT_REPO" });
    } else {
      urlToHit = `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/${
        props.editProjectId
      }/edit`;
      track("EDIT_PROJECT", {
        projectId: props.editProjectId,
      });
    }

    let repoPath = projectPathInput;
    if (props.provider === GitProvider.BITBUCKET) {
      repoPath = `${organizationPathInput}/${projectPathInput}`;
    }

    setIsCreatingProject(true);
    session?.getToken().then((supabaseJWT) => {
      axios
        .post(
          urlToHit,
          {
            repoName: projectNameInput,
            repoPath,
            framework: frameworkInput,
            mainFilePath: mainFilePathInput,
            srcFolderPath: srcFolderPath,
            customInstallCommand: customInstallCommandInput,
            devServerPort: devServerPortInput,
            customRunCommand: customRunCommandInput,
            customInstallExtraCommand: customInstallExtraCommandInput,
            nodeVersion: nodeVersionInput,
            packageManager: packageManagerInput,
            userId: userId,
            exampleComponentPath,
            projectSubfolderPath: projectSubfolderPathInput,
            disableAutoformat: !autoFormatEnabled,
            runMode: ProjectRunMode.CLOUD,
            base_branch: baseBranchInput,
            auto_install_off: autoInstallOff,
            provider: props.provider,
            gitlab_project_id:
              props.provider === GitProvider.GITLAB
                ? gitlabProjectPathInputId
                : null,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then((res) => {
          triggerRefetchProjects?.();

          const environmentVariables = {};
          envVariablesSet.forEach((envVariable) => {
            if (!envVariable?.value) {
              return;
            }

            if (envVariable?.value === SECRET_ENV_VARIABLE_VALUE) {
              return;
            }

            if (envVariable?.value === DELETE_ENV_VARIABLE_CONSTANT) {
              if (!isAdd) {
                environmentVariables[envVariable.key] = null;
              }

              return;
            }

            environmentVariables[envVariable.key] = envVariable.value;
          });

          const createdProject = res.data.project;
          const createCanvas = res.data.canvas;

          if (isAdd) {
            if (res.data?.project?.templateUserId) {
              track("TRACK_USED_RUNNING_TEMPLATE", {
                used: true,
                repoPath: projectPathInput,
              });
            } else {
              track("TRACK_USED_RUNNING_TEMPLATE", {
                used: false,
                repoPath: projectPathInput,
              });
            }
          }

          if (!Object.keys(environmentVariables).length) {
            toast.success(!isAdd ? "Edited project" : "Created project");
            props.close(createdProject, createCanvas);
            return;
          }

          axios
            .post(
              `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/${
                res.data?.project?.id
              }/envVariables`,
              environmentVariables,
              {
                headers: {
                  Authorization: `Bearer ${supabaseJWT}`,
                },
              }
            )
            .then(() => {
              toast.success(!isAdd ? "Edited project" : "Created project");
              props.close(createdProject, createCanvas);
              return;
            })
            .catch((err) => {
              logAxiosError(
                `Error setting environment variables for ${
                  isAdd ? "added" : "edited"
                } project`,
                err
              );
              toast.error(
                `${
                  isAdd ? "Created" : "Edited"
                } project but could not set environment variables. Error: ${getAxiosErorrMessage(
                  err
                )}`
              );
            });
        })
        .catch((err) => {
          logAxiosError(`Error ${isAdd ? "adding" : "editing"} project`, err);
          toast.error(
            `Could not ${
              isAdd ? "add" : "edit"
            } project, check if path is valid. Error: ${getAxiosErorrMessage(
              err
            )}`
          );
        })
        .finally(() => {
          setIsCreatingProject(false);
        });
    });
  };

  const handleEnvVariables = (variables) => {
    setEnvVariablesSet(variables);
  };

  const isAdd = !props.editProjectId;

  const navigate = useNavigate();

  const [showEnvironments, setshowEnvironments] = useState(false);

  const handleNext = () => {
    if (showEnvironments) {
      createProject();
    } else {
      setshowEnvironments(true);
    }
  };

  const handleConnectGithub = () => {
    if (props.provider !== GitProvider.GITHUB) return;
    if (!installClicked) {
      window.open(
        "https://github.com/apps/tempo-labs-ai/installations/new",
        "_blank"
      );
      setInstallClicked(true);
    } else {
      checkGithubPermissions(true);
    }
  };

  return (
    <div className="w-full max-w-lg">
      <div className="w-full">
        <div className="max-w-2xlr">
          {(isCreatingProject || isInitializing) && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 rounded-lg">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">
                  {isCreatingProject
                    ? (isAdd ? "Creating" : "Saving") + " project..."
                    : "Loading project data..."}
                </span>
              </div>
            </div>
          )}

          {props.provider === GitProvider.GITHUB && checkingGithub ? (
            <div className="flex flex-col items-center justify-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">
                Checking GitHub installation...
              </span>
            </div>
          ) : props.provider === GitProvider.GITHUB && !isGithubConnected ? (
            <>
              <div className="flex flex-col items-center justify-center gap-4">
                <h1 className="text-3xl font-medium text-secondary-n-50">
                  Install GitHub App
                </h1>
                <p className="text-sm font-normal text-secondary-n-400">
                  Install the Tempo GitHub app to get started.
                </p>
              </div>
              <div
                className="h-px w-full mt-6 mb-2"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, #131519 0%, #8A8A8C 47%, #131519 100%)",
                }}
              />
              <div className="flex items-center justify-center gap-2 py-4">
                <Button
                  variant="outline"
                  size="sm"
                  style={{
                    borderWidth: "1px",
                    borderColor: "var(--secondary-n-900)",
                    borderStyle: "solid",
                  }}
                  className=" flex items-center rounded-md gap-2 h-7 w-11 self-start"
                  onClick={() => {
                    navigate("/project-create/workflow-select");
                  }}
                >
                  Back
                </Button>
                <Button
                  size="lg"
                  className="text-xs  font-medium px-2 h-7 w-11 rounded-md text-secondary-n-50"
                  variant="primary"
                  isLoading={isCreatingProject}
                  disabled={isCreatingProject}
                  onClick={handleConnectGithub}
                >
                  {installClicked ? "Check" : "Install"}
                </Button>
              </div>
            </>
          ) : showEnvironments ? (
            <div className="flex flex-col items-center justify-center gap-4">
              <h1 className="text-3xl font-medium text-secondary-n-50">
                Set environment variables
              </h1>
              <p className="text-sm font-normal text-secondary-n-400">
                Add API keys and database info to run your project.
              </p>
            </div>
          ) : (props.provider === GitProvider.GITHUB && isGithubConnected) ||
            (props.provider === GitProvider.GITLAB && isGitlab) ||
            (props.provider === GitProvider.BITBUCKET && isBitbucket) ? (
            <div className="flex flex-col items-center justify-center gap-4">
              <h1 className="text-3xl font-medium text-secondary-n-50">
                Set up project details
              </h1>
              <p className="text-sm font-normal text-secondary-n-400">
                Select a repository and enter your project details.
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-4">
              <h1 className="text-3xl font-medium text-secondary-n-50">
                Connect Git Provider
              </h1>
              <p className="text-sm font-normal text-secondary-n-400">
                Please connect to{" "}
                {props.provider === GitProvider.GITLAB ? "GitLab" : "GitHub"} to
                continue.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/project-create/workflow-select")}
              >
                Back to Setup
              </Button>
            </div>
          )}

          <ScrollArea
            className={cn(
              "-mx-[1px] -mr-3 max-h-[calc(100vh-270px)] transition-all duration-300 ease-in-out overflow-auto",
              showEnvironments || isOpen ? "h-auto" : "h-[240px]"
            )}
          >
            {((props.provider === GitProvider.GITHUB && isGithubConnected) ||
              (props.provider === GitProvider.GITLAB && isGitlab) ||
              (props.provider === GitProvider.BITBUCKET && isBitbucket)) && (
              <div className="p-[1px] pr-3 mt-2">
                {showEnvironments ? (
                  <div className="flex flex-col gap-3 justify-center">
                    {envVariablesSet.map((envVariable, index) => {
                      if (envVariable.value === DELETE_ENV_VARIABLE_CONSTANT) {
                        return null;
                      }

                      const firstVisibleIndex = envVariablesSet.findIndex(
                        (v) => v.value !== DELETE_ENV_VARIABLE_CONSTANT
                      );

                      return (
                        <div className="flex items-end gap-2" key={index}>
                          <div className="w-full flex flex-col gap-1">
                            {index === firstVisibleIndex && (
                              <span className="text-secondary-n-400 text-2xs ml-2">
                                Key
                              </span>
                            )}
                            <Input
                              value={envVariable?.key}
                              onChange={(e) => {
                                setEnvVariablesSet((prev) => {
                                  const copy = [...prev];
                                  copy[index].key = e.currentTarget.value;
                                  return copy;
                                });
                              }}
                              placeholder={"key"}
                              className="text-xs placeholder:text-xs placeholder:text-secondary-n-600 w-full border border-transparent hover:border-secondary-n-600 focus:border-primary-0 rounded-md outline-none focus:outline-none h-8 justify-between px-2 font-normal bg-secondary-n-950"
                            />
                          </div>
                          <div className="w-full flex flex-col gap-1">
                            {index === firstVisibleIndex && (
                              <span className="text-secondary-n-400 text-2xs ml-2">
                                Value
                              </span>
                            )}
                            <Input
                              className="text-xs placeholder:text-xs placeholder:text-secondary-n-600 w-full border border-transparent hover:border-secondary-n-600 focus:border-primary-0 rounded-md outline-none focus:outline-none h-8 justify-between px-2 font-normal bg-secondary-n-950"
                              value={
                                envVariable?.value === SECRET_ENV_VARIABLE_VALUE
                                  ? "**************"
                                  : envVariable?.value
                              }
                              onChange={(e) => {
                                setEnvVariablesSet((prev) => {
                                  const copy = [...prev];
                                  copy[index].value = e.currentTarget.value;
                                  return copy;
                                });
                              }}
                              onFocus={() => {
                                if (
                                  envVariable.value ===
                                  SECRET_ENV_VARIABLE_VALUE
                                ) {
                                  setEnvVariablesSet((prev) => {
                                    const copy = [...prev];
                                    copy[index].value = "";
                                    return copy;
                                  });
                                }
                              }}
                              placeholder="value"
                              aria-label="env-variable-value"
                            />
                          </div>
                          {envVariablesSet.filter(
                            (v) => v.value !== DELETE_ENV_VARIABLE_CONSTANT
                          ).length > 1 && (
                            <Button
                              onClick={() => {
                                setEnvVariablesSet((prev) => {
                                  const copy = [...prev];
                                  copy[index].value =
                                    DELETE_ENV_VARIABLE_CONSTANT;
                                  return copy;
                                });
                              }}
                              variant="outline"
                              className="text-secondary-n-600 rounded-md border-none outline-none focus:outline-none h-8 justify-between px-2 font-normal bg-secondary-n-950"
                            >
                              <Trash className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                    <div className="flex flex-col gap-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEnvVariablesSet((prev) => {
                            const copy = [...prev];
                            copy.push({ key: "", value: "" });
                            return copy;
                          });
                        }}
                        style={{
                          borderWidth: "1px",
                          borderStyle: "solid",
                          borderColor: "var(--secondary-n-900)",
                        }}
                        size="lg"
                        className="w-min whitespace-nowrap rounded-md px-2 text-xs font-medium h-8"
                      >
                        <PlusIcon className="w-4 h-4 mr-2" />
                        Add another
                      </Button>
                      <div className="border-t border-secondary-n-900 py-4">
                        <EnvVariableHandler
                          isCreatingProject
                          mode="upload"
                          handleEnvVariables={handleEnvVariables}
                          envVariablesSet={envVariablesSet}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {inputsAndSetters.map((inputAndSetter, index) => {
                        if (inputAndSetter.optional) {
                          return null;
                        }
                        return (
                          <div className="relative" key={inputAndSetter.label}>
                            <div>
                              {inputAndSetter.label && (
                                <span className="ml-2 text-2xs font-medium text-secondary-n-400">
                                  {inputAndSetter.label}
                                </span>
                              )}
                              {inputAndSetter.renderInput ? (
                                <>{inputAndSetter.renderInput()}</>
                              ) : (
                                <Input
                                  value={inputAndSetter.value?.toString?.()}
                                  onChange={(e: any) => {
                                    inputAndSetter.setter(e.target.value);
                                  }}
                                  placeholder={`Choose ${
                                    inputAndSetter.label?.toLowerCase() || "one"
                                  }`}
                                  disabled={
                                    !(isAdd || inputAndSetter.editableOnEdit)
                                  }
                                  className={cn(
                                    "w-full rounded-md h-8 justify-between px-2 font-normal bg-secondary-n-950 text-xs border border-transparent hover:border-secondary-n-600 focus:border-primary-0",
                                    inputAndSetter.renderInput && "text-md"
                                  )}
                                />
                              )}
                            </div>
                            {index === 0 && (
                              <div className="absolute left-[calc(100%+4px)] top-1 transform -translate-x-1/2 z-50">
                                <span className="text-secondary-n-50">/</span>
                              </div>
                            )}
                            {inputAndSetter.renderHint
                              ? inputAndSetter.renderHint()
                              : null}
                          </div>
                        );
                      })}
                    </div>
                    <Collapsible
                      open={isOpen}
                      onOpenChange={(value) => setIsOpen(value)}
                      className="w-full border-2 border-secondary-n-950 rounded-md mt-6"
                    >
                      <CollapsibleTrigger className="w-full">
                        <Button
                          variant="outline"
                          role="combobox"
                          className={cn(
                            "w-full rounded-md  h-8 justify-between px-3  bg-secondary-n-1000 border border-secondary-n-950",
                            projectPathInput && "text-md"
                          )}
                        >
                          <span className="text-ellipsis  text-secondary-n-50 text-xs line-clamp-1 text-left">
                            Advanced
                          </span>
                          {isOpen ? (
                            <ChevronUp className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          ) : (
                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="gap-4 px-4 pb-2">
                          {inputsAndSetters.map((inputAndSetter) => {
                            if (!inputAndSetter.optional) {
                              return;
                            }

                            return (
                              <div
                                className={cn(
                                  inputAndSetter.renderInput &&
                                    "flex items-center justify-between"
                                )}
                                key={inputAndSetter.label}
                              >
                                <span
                                  className={cn(
                                    "ml-2",
                                    inputAndSetter.renderInput
                                      ? "text-secondary-n-50 text-xs mr-2"
                                      : "text-secondary-n-400 text-2xs font-medium"
                                  )}
                                >
                                  {inputAndSetter.label}
                                </span>
                                {inputAndSetter.renderInput ? (
                                  <div className="mt-2">
                                    {inputAndSetter.renderInput()}
                                  </div>
                                ) : (
                                  <Input
                                    placeholder={
                                      inputAndSetter?.infoTooltip || ""
                                    }
                                    value={inputAndSetter.value?.toString?.()}
                                    onChange={(e: any) => {
                                      inputAndSetter.setter(e.target.value);
                                    }}
                                    className={cn(
                                      "placeholder:text-xs text-xs placeholder:text-secondary-n-600 w-full border border-transparent hover:border-secondary-n-600 focus:border-primary-0 rounded-md outline-none focus:outline-none h-8 justify-between px-2 font-normal bg-secondary-n-950",
                                      inputAndSetter.renderInput && "text-md"
                                    )}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
          {((props.provider === GitProvider.GITHUB && isGithubConnected) ||
            (props.provider === GitProvider.GITLAB && isGitlab) ||
            (props.provider === GitProvider.BITBUCKET && isBitbucket)) && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Button
                variant="outline"
                size="sm"
                style={{
                  borderWidth: "1px",
                  borderColor: "var(--secondary-n-900)",
                  borderStyle: "solid",
                }}
                className=" flex items-center rounded-md gap-2 h-7 w-11 self-start"
                onClick={() => {
                  showEnvironments
                    ? setshowEnvironments(false)
                    : navigate("/project-create/workflow-select");
                }}
              >
                Back
              </Button>
              <Button
                size="lg"
                className="text-xs font-medium px-2 h-7 rounded-md w-11 text-secondary-n-50"
                variant="primary"
                isLoading={isCreatingProject}
                disabled={isCreatingProject}
                onClick={handleNext}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AINewProjectPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const provider = searchParams.get("provider");

  // Validate provider parameter
  const validProvider =
    provider === GitProvider.GITHUB ||
    provider === GitProvider.GITLAB ||
    provider === GitProvider.BITBUCKET
      ? (provider as GitProvider)
      : GitProvider.GITHUB;

  return (
    <ProjectCreateLayout showProjectName={true} hideCreateProject={true}>
      <div className="flex flex-col flex-1 justify-center items-center w-full text-md overflow-auto relative max-w-screen-lg mx-8 gap-3">
        <AddEditProject
          close={(project, canvas) => {
            if (!canvas?.id) {
              navigate(-1);
              return;
            }
            navigate(`/canvases/${canvas.id}/editor`);
          }}
          provider={validProvider}
        />
      </div>
    </ProjectCreateLayout>
  );
};

export default AINewProjectPage;
